import { type RefObject, useEffect, useRef } from 'react'

const IGNORE =
  'input, textarea, select, [contenteditable]:not([contenteditable="false"]), [draggable="true"], [data-drag-handle], [aria-roledescription="sortable"], [data-swipe-x]'

function ignoredTarget(target: EventTarget | null, root: Element) {
  if (!(target instanceof Element)) return true
  const hit = target.closest(IGNORE)
  if (hit && root.contains(hit)) return true
  for (let el: Element | null = target; el && el !== root; el = el.parentElement) {
    if (el.scrollWidth > el.clientWidth && /auto|scroll/.test(getComputedStyle(el).overflowX))
      return true
  }
  return false
}

type Opts = {
  enabled: boolean
  count: number
  index: () => number
  onLock: () => void
  onDrag: (dx: number, width: number) => void
  onRelease: (target: number) => void
}

// Leaderboard-style touch pager: horizontal drags past 8px lock the gesture, rubber-band
// at the ends, and settle on the neighbour past 25% width or on a fling. Moves are
// reported imperatively so the caller can paint without re-rendering per frame.
export function useSwipePager(ref: RefObject<HTMLElement | null>, opts: Opts) {
  const optsRef = useRef(opts)
  optsRef.current = opts

  useEffect(() => {
    const el = ref.current
    if (!el || !opts.enabled) return
    let g: {
      x: number
      y: number
      lastX: number
      lastT: number
      v: number
      w: number
      locked: boolean
    } | null = null

    const onStart = (e: TouchEvent) => {
      const t = e.touches[0]
      g =
        e.touches.length === 1 && !ignoredTarget(e.target, el)
          ? {
              x: t.clientX,
              y: t.clientY,
              lastX: t.clientX,
              lastT: e.timeStamp,
              v: 0,
              w: el.clientWidth || 1,
              locked: false,
            }
          : null
    }
    const onMove = (e: TouchEvent) => {
      if (!g) return
      if (e.touches.length !== 1) {
        if (g.locked) optsRef.current.onRelease(optsRef.current.index())
        g = null
        return
      }
      const t = e.touches[0]
      const dx = t.clientX - g.x
      const dy = t.clientY - g.y
      if (!g.locked) {
        if (Math.abs(dx) <= 8 && Math.abs(dy) <= 8) return
        if (Math.abs(dx) <= Math.abs(dy)) {
          g = null
          return
        }
        g.locked = true
        optsRef.current.onLock()
      }
      if (e.cancelable) e.preventDefault()
      const dt = e.timeStamp - g.lastT
      if (dt > 0) g.v = 0.8 * ((t.clientX - g.lastX) / dt) + 0.2 * g.v
      g.lastX = t.clientX
      g.lastT = e.timeStamp
      const { index, count, onDrag } = optsRef.current
      const next = index() + (dx < 0 ? 1 : -1)
      onDrag(next >= 0 && next < count ? dx : dx / 3, g.w)
    }
    const onEnd = (e: TouchEvent) => {
      if (!g?.locked) {
        g = null
        return
      }
      const dx = g.lastX - g.x
      const v = e.timeStamp - g.lastT < 100 ? g.v : 0
      const flung = Math.abs(v) > 0.5 && Math.sign(v) === Math.sign(dx)
      const go = e.type === 'touchend' && (Math.abs(dx) > g.w * 0.25 || flung)
      const { index, count, onRelease } = optsRef.current
      const i = index()
      onRelease(Math.max(0, Math.min(count - 1, i + (go ? (dx < 0 ? 1 : -1) : 0))))
      g = null
    }

    el.addEventListener('touchstart', onStart, { passive: true })
    el.addEventListener('touchmove', onMove, { passive: false })
    el.addEventListener('touchend', onEnd)
    el.addEventListener('touchcancel', onEnd)
    return () => {
      el.removeEventListener('touchstart', onStart)
      el.removeEventListener('touchmove', onMove)
      el.removeEventListener('touchend', onEnd)
      el.removeEventListener('touchcancel', onEnd)
    }
  }, [ref, opts.enabled])
}
